import React, { useState, useEffect, useRef, useCallback } from "react";
import { ChevronDown, X, Loader } from "lucide-react";

export interface SelectOption {
  value: string;
  label: string;
}

interface Props {
  value?: string;
  displayValue?: string;
  onChange: (value: string, label: string) => void;
  fetchOptions: (
    search: string,
    page: number,
  ) => Promise<{ options: SelectOption[]; hasMore: boolean }>;
  placeholder?: string;
  clearable?: boolean;
  size?: "sm" | "md";
  disabled?: boolean;
  error?: boolean;
}

const SearchableSelect: React.FC<Props> = ({
  value,
  displayValue,
  onChange,
  fetchOptions,
  placeholder = "Search…",
  clearable = true,
  size = "sm",
  disabled = false,
  error = false,
}) => {
  const [isOpen, setIsOpen]         = useState(false);
  const [inputText, setInputText]   = useState("");
  const [options, setOptions]       = useState<SelectOption[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore]       = useState(false);
  const [loading, setLoading]       = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef     = useRef<HTMLInputElement>(null);
  const sentinelRef  = useRef<HTMLDivElement>(null);
  const debounceRef  = useRef<ReturnType<typeof setTimeout>>();

  // ── Fetch helpers ─────────────────────────────────────────────────────────

  const load = useCallback(
    async (q: string, page: number, append = false) => {
      if (page === 1) setLoading(true);
      else setLoadingMore(true);
      try {
        const result = await fetchOptions(q, page);
        setOptions((prev) => (append ? [...prev, ...result.options] : result.options));
        setHasMore(result.hasMore);
        setCurrentPage(page);
      } catch {
        /* silent */
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [fetchOptions],
  );

  // ── Open / close ──────────────────────────────────────────────────────────

  const openWith = (q = "") => {
    setIsOpen(true);
    setInputText(q);
    setOptions([]);
    load(q, 1, false);
  };

  const close = () => {
    setIsOpen(false);
    // Restore display value in input when closed
    setInputText("");
  };

  // ── Debounced search while open ───────────────────────────────────────────

  useEffect(() => {
    if (!isOpen) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(inputText, 1, false), 300);
    return () => clearTimeout(debounceRef.current);
  }, [inputText, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Infinite scroll ───────────────────────────────────────────────────────

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || loadingMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) load(inputText, currentPage + 1, true);
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, currentPage, inputText, load]);

  // ── Outside click ─────────────────────────────────────────────────────────

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Selection ─────────────────────────────────────────────────────────────

  const select = (opt: SelectOption) => {
    onChange(opt.value, opt.label);
    setIsOpen(false);
    setInputText("");
  };

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("", "");
    setInputText("");
    inputRef.current?.focus();
    openWith("");
  };

  // ── Keyboard ──────────────────────────────────────────────────────────────

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") close();
    if (e.key === "ArrowDown" && !isOpen) openWith(inputText);
  };

  // ── Styles ────────────────────────────────────────────────────────────────

  const sizeClass = size === "sm"
    ? "h-8 px-2.5 text-sm"
    : "h-10 px-3 text-base";

  const borderClass = error
    ? "border-red-400"
    : isOpen
      ? "border-blue-500 ring-2 ring-blue-100"
      : "border-gray-300 hover:border-gray-400";

  return (
    <div ref={containerRef} className="relative w-full">
      {/* ── Single input (trigger + search) ── */}
      <div className={`flex items-center border rounded-md bg-white transition-all ${borderClass} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? inputText : (displayValue ?? "")}
          onChange={(e) => {
            setInputText(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => {
            if (!isOpen) openWith("");
          }}
          onKeyDown={handleKeyDown}
          placeholder={isOpen ? "Search…" : placeholder}
          disabled={disabled}
          readOnly={disabled}
          className={`flex-1 bg-transparent outline-none placeholder-gray-400 ${sizeClass} ${disabled ? "cursor-not-allowed" : ""}`}
        />

        <div className="flex items-center pr-2 gap-0.5 shrink-0">
          {loading && <Loader className="h-3.5 w-3.5 text-gray-400 animate-spin" />}
          {clearable && value && !disabled && !loading && (
            <button
              type="button"
              onClick={clear}
              className="p-0.5 text-gray-400 hover:text-gray-600 rounded"
              tabIndex={-1}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={() => (isOpen ? close() : openWith(inputText))}
            disabled={disabled}
            tabIndex={-1}
            className="p-0.5 text-gray-400 hover:text-gray-600"
          >
            <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>

      {/* ── Dropdown ── */}
      {isOpen && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
          <div className="max-h-52 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-400">
                <Loader className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : options.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-400">No results found</p>
            ) : (
              <>
                {options.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()} // prevent input blur
                    onClick={() => select(opt)}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                      opt.value === value
                        ? "bg-blue-50 text-blue-700 font-medium"
                        : "text-gray-800 hover:bg-gray-50"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}

                {/* Infinite scroll sentinel – only when more pages exist */}
                {hasMore && (
                  <div ref={sentinelRef} className="flex justify-center py-2">
                    {loadingMore && <Loader className="h-4 w-4 text-gray-400 animate-spin" />}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
